import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  ClickAwayListener,
  Grow,
  IconButton,
  InputBase,
  Modal,
  Paper,
  Popover,
  Portal,
  styled,
  Theme,
} from "@mui/material";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../util/aKaunGeneralUtil";
import AddIcon from "@mui/icons-material/Add";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { MobileOnly } from "../util/mobileOnly";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { RoundBottomNavigationButton } from "./rosebudButtomNavigationButton";
import SearchIcon from "@mui/icons-material/Search";
import { useScreenSize } from "../util/useScreenSize";
import { ClearIcon } from "./buttons/clearIcon";
import { FeatureWithName } from "fe-web-components/autocomplete";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { PopoverOrigin, PopoverPosition } from "@mui/material/Popover/Popover";

//region [[ Styles ]]

const SearchModalView = styled("div")({
  display: "flex",
});

const OpenButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
}));

const ModalContainer = styled(
  ({
    anchorEl,
    ...props
  }: {
    anchorEl: any;
    open: boolean;
    children: any;
    className?: string;
    onClose: () => void;
    anchorOrigin?: PopoverOrigin;
    anchorPosition?: PopoverPosition;
    transformOrigin?: PopoverOrigin;
  }) =>
    anchorEl ? (
      <Popover anchorEl={anchorEl} {...props} />
    ) : (
      <Modal {...props} />
    ),
)(({ theme }) => ({
  outline: "none",
}));

const PopoverContent = styled(Paper)(
  ({ theme, anchorEl }: { theme?: Theme; anchorEl: HTMLElement }) => ({
    display: "flex",
    flexFlow: "column",
    "&:active, &:focus": { outline: "none" },
    [theme.breakpoints.down(MobileView)]: {
      //TODO:
      height: "100%",
      background: KahunColorMap.White,
      borderRadius: 0,
    },
    [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
      width: anchorEl?.clientWidth || "auto",
      minWidth: "500px",
      height: "45vh",
    },
  }),
);

const ModalContent = styled(PopoverContent)(({ theme }) => ({
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    top: "50%",
    left: "50%",
    width: "50%",
    height: "50%",
    transform: `translate(50%, 50%)`,
  },
}));

const Content = styled(
  ({ anchorEl, ...props }: { anchorEl: any; open: boolean; children: any }) =>
    anchorEl ? (
      <Popover anchorEl={anchorEl} {...props} />
    ) : (
      <Modal {...props} />
    ),
)(({ theme }) => ({}));

const InputField = styled(InputBase)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.down(MobileView)]: {
    marginLeft: "10px",
    //TODO:
  },
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    borderRadius: "10px",
    border: `1px solid ${KahunColorMap.Divider}`,
    padding: theme.spacing(1),
  },
}));

export const ResultsLeftPadding = 40;

const InputArea = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  padding: theme.spacing(2),
  position: "relative",
  boxShadow: "1px 1px 2px rgb(0 0 0 / 0.12)",
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: `${ResultsLeftPadding}px`,
  },
}));

const BackContainer = styled(IconButton)({
  position: "absolute",
  padding: "4px",
  left: "7px",
});

const ResultsList = styled("div")(({ theme }) => ({
  ...scrollableStyle,
  padding: `0 ${theme.spacing(1)}px`,
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: "13px",
    paddingRight: "13px",
  },
}));

const ResultItem = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: `${ResultsLeftPadding}px`,
  },
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    paddingLeft: theme.spacing(2),
  },
}));

const DoneSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row-reverse",
  boxShadow: "-1px -1px 2px rgb(0 0 0 / 0.12)",
  padding: "10px",
  paddingLeft: "12px",
  paddingRight: "12px",
}));

const DoneButton = styled(RoundBottomNavigationButton)(
  ({ theme, dim }: { theme?: Theme; dim?: "true" | "false" }) => ({
    width: "33.3%",
    [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
      width: "20%",
    },
    background: dim === "true" ? "#e0e0e0" : KahunColorMap.PrimaryDim,
    color: dim === "true" ? KahunColorMap.Default : KahunColorMap.White,
  }),
);

const NoResults = styled("div")(({ theme }) => ({
  margin: `0  -${theme.spacing(2)}px`,
  height: "100%",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  color: KahunColorMap.Default,
}));

const NoTermEntered = styled(NoResults)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.1)",
}));

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  color: KahunColorMap.Divider,
  marginRight: "5px",
  [theme.breakpoints.up("xs")]: {
    "font-size": "30px",
  },
  [theme.breakpoints.down("xs")]: {
    "font-size": "larger",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface SearchResult<T> {
  /***
   * maintained by SearchModal and indicates if the item was selected or not
   */
  readonly selected?: boolean;
  value: T;
}

export interface SearchModalProps<T> {
  className?: string;
  onValueChange: (input: string) => void;
  renderResult: (item: SearchResult<T>) => React.ReactNode;
  buttonIcon?: () => void;
  onItemSelected?: (item: T) => void;
  open?: boolean;
  multiple?: boolean;
  searchIcon?: React.ReactNode;
  loading?: boolean;
  openSearchButton?: React.ReactElement;
  results: FeatureWithName[];
  onDone?: () => void;
  hideOpenSearchButton?: boolean;
  children?: any;
  placeholder?: string;
  anchorEl?: HTMLElement | null;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const toSearchResult = (i, index) => ({
  id: i?.id || index,
  selected: false,
  value: i,
});
//endregion [[ Functions ]]

//region [[ Texts ]]
const SEARCH_START_TYPING_TEXT = "Start typing to see results";
const NO_SEARCH_RESULTS_TEXT = "no results match your term";
const DEFAULT_SEARCH_PLACEHOLDER = "Search...";
const DONE_BUTTON_TEXT = "Done";
//endregion [[ Texts]]

export const SearchModal = <T extends any>({
  onValueChange,
  onItemSelected,
  multiple,
  searchIcon,
  loading,
  results,
  onDone,
  ...props
}: SearchModalProps<T>) => {
  const { isMobile } = useScreenSize();

  const [isModalOpen, setIsModalOpen] = useState(props.open || false);

  const [resultsList, setResultsList] = useState<SearchResult<T>[]>(null);

  const [searchText, setSearchText] = useState<string>("");

  useEffect(() => {
    setIsModalOpen(props.open || false);
  }, [props.open]);

  useEffect(() => {
    setResultsList(results?.map(toSearchResult));
  }, [results]);

  const handleInputChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e?.target?.value || "");
      onValueChange(e?.target?.value || "");
    },
    [onValueChange],
  );

  const handleDoneClicked = useCallback(() => {
    // Close modal, clear results and exit
    setResultsList(null);
    setIsModalOpen(false);
    setSearchText("");
    if (onDone) onDone();
  }, [onDone]);

  const handleSearchIconClick = useCallback(() => {
    if (isModalOpen) {
      handleDoneClicked();
      return setIsModalOpen(false);
    }
    setIsModalOpen(true);
  }, [isModalOpen, handleDoneClicked]);

  const handleClickAway = useCallback(() => {
    handleDoneClicked();
  }, [handleDoneClicked]);

  const createItemSelectedHandler = useCallback(
    (item: SearchResult<T>) => (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      // Fire event
      onItemSelected(item.value);

      // If not a multiple selection mode, close modal
      if (!multiple) return handleDoneClicked();

      // Mark item as selected in results
      const itemIndex = resultsList.findIndex((i) => i === item);
      const newResultsArray = [
        ...resultsList.slice(0, itemIndex),
        { ...resultsList[itemIndex], selected: true },
        ...resultsList.slice(itemIndex + 1),
      ];
      // Update results
      setResultsList(newResultsArray);
    },
    [onItemSelected, multiple, setResultsList, handleDoneClicked, resultsList],
  );

  const getSearchIcon = useMemo(
    () => (isMobile ? null : searchIcon ? searchIcon : <StyledSearchIcon />),
    [isMobile, searchIcon],
  );

  const handleClearTerm = useCallback(() => {
    handleInputChanged(null);
    setSearchText("");
  }, [handleInputChanged, setSearchText]);

  const getLoadingOrClearSign = useMemo(
    () =>
      loading ? (
        <CircularProgress color="inherit" size={20} />
      ) : resultsList ? (
        <ClearIcon onClick={handleClearTerm} />
      ) : (
        <></>
      ),
    [loading, resultsList, handleClearTerm],
  );

  const selectedCount = useMemo(
    () => resultsList?.filter((r) => r.selected)?.length || 0,
    [resultsList],
  );

  const anchor = useMemo(() => (isMobile ? null : props.anchorEl), [
    isMobile,
    props.anchorEl,
  ]);

  return (
    <SearchModalView className={props.className}>
      {!props.hideOpenSearchButton && (
        <OpenButtonContainer
          className={"open-button-container"}
          onClick={handleSearchIconClick}
        >
          {props.openSearchButton ? props.openSearchButton : <AddIcon />}
        </OpenButtonContainer>
      )}
      <ModalContainer
        className={"modal-container"}
        open={isModalOpen}
        anchorEl={anchor}
        onClose={handleClickAway}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Grow in={isModalOpen} mountOnEnter unmountOnExit timeout={500}>
          <ClickAwayListener onClickAway={handleClickAway}>
            <Content anchorEl={anchor} open={true}>
              <InputArea className={"input-area"}>
                <MobileOnly>
                  <BackContainer onClick={handleSearchIconClick}>
                    <ArrowBackIcon />
                  </BackContainer>
                </MobileOnly>
                <InputField
                  autoFocus
                  placeholder={props.placeholder || DEFAULT_SEARCH_PLACEHOLDER}
                  onChange={handleInputChanged}
                  startAdornment={getSearchIcon}
                  endAdornment={getLoadingOrClearSign}
                  value={searchText}
                />
              </InputArea>
              <ResultsList>
                {!resultsList && (
                  <NoTermEntered>{SEARCH_START_TYPING_TEXT}</NoTermEntered>
                )}
                {resultsList &&
                  resultsList.map((item, index) => (
                    <ResultItem
                      key={index}
                      onClick={createItemSelectedHandler(item)}
                    >
                      {(props.renderResult && props.renderResult(item)) ||
                        item?.value.toString()}
                    </ResultItem>
                  ))}
                {resultsList && !resultsList.length && !loading && (
                  <NoResults>{NO_SEARCH_RESULTS_TEXT}</NoResults>
                )}
              </ResultsList>
              {multiple && (
                <DoneSection>
                  <DoneButton
                    main={"true"}
                    onClick={handleDoneClicked}
                    dim={selectedCount === 0 ? "true" : "false"}
                  >
                    {DONE_BUTTON_TEXT}
                  </DoneButton>
                </DoneSection>
              )}
              <Portal>{props.children}</Portal>
            </Content>
          </ClickAwayListener>
        </Grow>
      </ModalContainer>
    </SearchModalView>
  );
};
